import { useEffect } from "react";
import moment from "moment";
import {
  Permit,
  triggerRejectPermitted,
  triggerCancelPermitted,
  triggerCompletePermitted,
  triggerExtendPermitted,
  triggerRequestPermitted,
  triggerSavePermitted,
  triggerSubmitPermitted,
  triggerUndoCancellationPermitted,
  triggerApprovePermitted,
} from "../models/Permit";
import PermitView from "../components/PermitView";
import { useRejectPermit } from "../operations/mutations/rejectPermit";
import { useApprovePermit } from "../operations/mutations/approvePermit";
import { useCancelPermit } from "../operations/mutations/cancelPermit";
import { useUndoCancelledPermit } from "../operations/mutations/undoCancelledPermit";
import { useExtendPermit } from "../operations/mutations/extendPermit";
import { useCompletePermit } from "../operations/mutations/completePermit";
import { usePermitRegisterPolicies } from "../hooks/usePermitRegisterPolicies";
import { useLazyGetPermitToDig } from "../operations/queries/getPermitToDig";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { StateKeyList } from "../models/StateKey";
import { useHistory } from "react-router";
import { useAppContext } from "../hooks/useAppContext";
import { useRecreateEsriSnapshot } from "../operations/mutations/recreateEsriSnapshot";
import { SystemDocumentType } from "../models/SystemDocumentType";
import { EsriIntegrationTypes } from "../models/EsriIntegrationType";

interface Props {
  showPermitBoundariesOnMap: boolean;
  permitId?: string | null;
  onClose: () => void;
  onPermitRejected?: (permit: Permit) => void;
  onPermitApproved?: (permit: Permit) => void;
  onPermitCancelled?: (permit: Permit) => void;
  onPermitUndoCancellation?: (permit: Permit) => void;
  onPermitExtended?: (permit: Permit) => void;
  onPermitCompleted?: (permit: Permit) => void;
}

const PermitViewContainer = ({
  showPermitBoundariesOnMap,
  permitId,
  onClose,
  onPermitApproved,
  onPermitCompleted,
  onPermitExtended,
  onPermitRejected,
  onPermitCancelled,
  onPermitUndoCancellation,
}: Props) => {
  const history = useHistory();
  const { currentOrganization, organizationName, projectName, permitRegisterTitle } = useAppContext();
  const currentUser = useCurrentUser();

  const { mutate: rejectPermitMutate, status: rejectPermitStatus } = useRejectPermit();
  const { mutate: approvePermitMutate, status: approvePermitStatus } = useApprovePermit();
  const { mutate: cancelPermitMutate, status: cancelPermitStatus } = useCancelPermit();
  const { mutate: undoCancelledPermitMutate, status: undoCancelledPermitStatus } = useUndoCancelledPermit();
  const { mutate: extendPermitMutate, status: extendPermitStatus } = useExtendPermit();
  const { mutate: completePermitMutate, status: completePermitStatus } = useCompletePermit();
  const { getPermitToDig: getPermit, data: permit, refetch: refetchPermitToDig } = useLazyGetPermitToDig();

  const {
    authorizedToAddPermit,
    authorizedToRequestPermit,
    authorizedToDownloadPermitPdf,
    authorizedToApprovePermit,
    authorizedToExtendPermit,
    authorizedToCompletePermit,
    authorizedToCancelOwnPermit,
    authorizedToCancelPermit,
    authorizedToUndoCancellationOwnPermit,
    authorizedToUndoCancellationPermit,
    authorizedToAddDocument,
  } = usePermitRegisterPolicies(permit?.type?.id);
  const { mutate: recreateEsriSnapshotMutate, status: recreateEsriSnapshotStatus } = useRecreateEsriSnapshot();

  useEffect(() => {
    if (permitId) {
      getPermit({ variables: { id: permitId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitId]);

  const handleRejectPermit = (comments: string) => {
    if (permit) {
      rejectPermitMutate({
        variables: {
          id: permit.id,
          comments,
          permitRegisterId: permit.permitRegister.id,
          permitTypeId: permit.type.id,
        },
      });
    }
  };

  const handleApprovePermit = () => {
    if (permit) {
      approvePermitMutate({
        variables: {
          id: permit.id,
          permitRegisterId: permit.permitRegister.id,
          permitTypeId: permit.type.id,
        },
      });
    }
  };

  const handleCancelPermit = () => {
    if (permit) {
      cancelPermitMutate({
        variables: {
          id: permit.id,
        },
      });
    }
  };

  const handleUndoCancelledPermit = () => {
    if (permit) {
      undoCancelledPermitMutate({
        variables: {
          id: permit.id,
        },
      });
    }
  };

  const handleExtendPermit = (to: Date, comments: string) => {
    if (permit) {
      extendPermitMutate({
        variables: {
          id: permit.id,
          to,
          comments,
          permitRegisterId: permit.permitRegister.id,
          permitTypeId: permit.type.id,
        },
      });
    }
  };

  const handleCompletePermit = () => {
    if (permit) {
      completePermitMutate({
        variables: {
          id: permit.id,
          permitRegisterId: permit.permitRegister.id,
          permitTypeId: permit.type.id,
        },
      });
    }
  };

  const cancelButtonVisibility = (): boolean => {
    if (!permit) {
      return false;
    }

    if (!triggerCancelPermitted(permit)) {
      return false;
    }

    if (permit.state.key === StateKeyList.IN_PROGRESS.key && authorizedToCancelPermit) {
      return true;
    }

    if (
      (permit.state.key === StateKeyList.REJECTED.key || permit.state.key === StateKeyList.REQUEST_IN_PROGRESS.key) &&
      authorizedToCancelOwnPermit &&
      permit.createdBy.user.id === currentUser.user?.id
    ) {
      return true;
    }

    return false;
  };

  const undoCancellationButtonVisibility = (): boolean => {
    if (!permit) {
      return false;
    }

    if (!triggerUndoCancellationPermitted(permit)) {
      return false;
    }

    if (
      authorizedToUndoCancellationPermit ||
      (authorizedToUndoCancellationOwnPermit && permit.createdBy.user.id === currentUser.user?.id)
    ) {
      return true;
    }

    return false;
  };

  const editButtonVisibility = (): boolean => {
    if (!permit) {
      return false;
    }

    // Edit Rejected permits by permit creator (requester)
    if (triggerRequestPermitted(permit) && permit.createdBy.user.id === currentUser.user?.id) {
      return true;
    }

    // Edit InProgress permits by permit manager (some who can submit permits)
    if (authorizedToAddPermit && (triggerSavePermitted(permit) || triggerSubmitPermitted(permit))) {
      return true;
    }

    return false;
  };

  const regenerateEsriSnapshotVisibility = (): boolean => {
    if (
      !currentOrganization?.esriConfig?.integrationTypeId ||
      currentOrganization.esriConfig.integrationTypeId === EsriIntegrationTypes.NONE.id
    ) {
      return false;
    }

    if (!authorizedToAddPermit) {
      return false;
    }

    if (!permit) {
      return false;
    }

    if (permit.state.key !== StateKeyList.IN_PROGRESS.key && permit.state.key !== StateKeyList.READY_TO_ISSUE.key) {
      return false;
    }

    if (!permit.areas || permit.areas.length === 0 || !permit.areas.some((a) => !!a.polygon)) {
      return false;
    }

    if (permit.documents.some((d) => d.type?.key === SystemDocumentType.ARC_GIS_DRAWING.key)) {
      return false;
    }

    return true;
  };

  const extendButtonVisibility = (): boolean => {
    if (!permit) {
      return false;
    }

    if (!authorizedToExtendPermit || !triggerExtendPermitted(permit)) {
      return false;
    }

    if (!permit.validityDuration?.to) {
      return true;
    }

    const permitEndDate = moment(permit.validityDuration.to).startOf("day");
    const validExpiryDate = permit.validityDuration.endsNextDay ? permitEndDate.clone().add(1, "day") : permitEndDate;

    const daysSincePermitExpired = moment().startOf("day").diff(validExpiryDate, "days");

    return daysSincePermitExpired <= 1;
  };

  const renewButtonVisibility = (): boolean => {
    if (!permit) {
      return false;
    }

    // Renew Cancelled, Completed, Expired or Issued permits by permit manager or permit requester
    if (
      (authorizedToAddPermit || authorizedToRequestPermit) &&
      [
        StateKeyList.EXPIRED.key,
        StateKeyList.CANCELLED.key,
        StateKeyList.COMPLETED.key,
        StateKeyList.ISSUED.key,
      ].includes(permit.state.key)
    ) {
      return true;
    }

    return false;
  };

  const handleAsBuiltAdded = () => {
    refetchPermitToDig && refetchPermitToDig();
  };

  const handleEditPermit = () => {
    if (organizationName && projectName && permitRegisterTitle && permit) {
      if (authorizedToAddPermit) {
        history.push(
          `/${organizationName}/projects/${projectName}/registers/${permitRegisterTitle}/permits/add-permit/${permit.id}`,
        );
      } else if (authorizedToRequestPermit) {
        history.push(
          `/${organizationName}/projects/${projectName}/registers/${permitRegisterTitle}/permits/request-permit/${permit.id}`,
        );
      }
    }
  };

  const handleRenewPermit = () => {
    if (organizationName && projectName && permitRegisterTitle && permit) {
      if (authorizedToAddPermit) {
        history.push(
          `/${organizationName}/projects/${projectName}/registers/${permitRegisterTitle}/permits/add-permit/renew/${permit.id}`,
        );
      } else if (authorizedToRequestPermit) {
        history.push(
          `/${organizationName}/projects/${projectName}/registers/${permitRegisterTitle}/permits/request-permit/renew/${permit.id}`,
        );
      }
    }
  };

  const handleRecreateEsriSnapshot = () => {
    if (!permitId) {
      return;
    }
    recreateEsriSnapshotMutate({
      variables: {
        id: permitId,
      },
    });
  };

  return (
    <PermitView
      showPermitBoundariesOnMap={
        showPermitBoundariesOnMap && (permit?.template?.features?.gisBoundary?.enabled ?? false)
      }
      downloadPdfButtonVisible={authorizedToDownloadPermitPdf}
      rejectButtonVisible={authorizedToApprovePermit && permit && triggerRejectPermitted(permit)}
      approveButtonVisible={authorizedToApprovePermit && permit && triggerApprovePermitted(permit)}
      cancelButtonVisible={cancelButtonVisibility()}
      undoCancellationButtonVisible={undoCancellationButtonVisibility()}
      extendButtonVisible={extendButtonVisibility()}
      completeButtonVisible={authorizedToCompletePermit && permit && triggerCompletePermitted(permit)}
      editButtonVisible={editButtonVisibility()}
      renewButtonVisible={renewButtonVisibility()}
      permit={permit}
      organization={currentOrganization}
      onClose={onClose}
      onEditPermit={handleEditPermit}
      onRenewPermit={handleRenewPermit}
      onRejectPermit={handleRejectPermit}
      rejectPermitStatus={rejectPermitStatus}
      onApprovePermit={handleApprovePermit}
      approvePermitStatus={approvePermitStatus}
      onCancelPermit={handleCancelPermit}
      cancelPermitStatus={cancelPermitStatus}
      onUndoCancelledPermit={handleUndoCancelledPermit}
      undoCancelledPermitStatus={undoCancelledPermitStatus}
      onExtendPermit={handleExtendPermit}
      extendPermitStatus={extendPermitStatus}
      onCompletePermit={handleCompletePermit}
      completePermitStatus={completePermitStatus}
      onPermitRejected={onPermitRejected}
      onPermitApproved={onPermitApproved}
      onPermitCancelled={onPermitCancelled}
      onPermitUndoCancellation={onPermitUndoCancellation}
      onPermitExtended={onPermitExtended}
      onPermitCompleted={onPermitCompleted}
      onAsBuiltAdded={handleAsBuiltAdded}
      addNewDocumentButtonVisible={authorizedToAddDocument}
      asBuiltCompletionDeclarations={permit?.template?.features?.asBuilt?.asBuiltCompletionDeclarations}
      onRecreateEsriSnapshot={handleRecreateEsriSnapshot}
      reCreateEsriSnapshotVisible={regenerateEsriSnapshotVisibility()}
      recreateEsriSnapshotStatus={recreateEsriSnapshotStatus}
      showAffectedServices={permit?.template?.features?.affectedServices?.enabled}
      showAsBuiltRequired={permit?.template?.features?.asBuilt?.enabled}
      showCalibrationCertificate={permit?.template?.features?.catAndGenny?.enabled}
      gangMembersEnabled={permit?.template?.features?.gangMembers?.enabled}
      showSitePhotos={permit?.template?.features?.sitePhoto?.enabled}
    ></PermitView>
  );
};

export default PermitViewContainer;
